import type { CallType } from '@/api/types';
import { getConfig } from '@/config/config';

/**
 * The media side of a call (research.md §6), behind one interface like
 * Android's callProvider: signaling goes over the calls socket, and the
 * provider only carries sound and picture for a room the backend names.
 */
/**
 * Why a call could not start or connect, in terms the member can act on:
 * a blocked or missing microphone or camera, a browser that cannot make calls,
 * calls not set up on the server, or a connection failure.
 */
export type CallErrorKind =
  | 'microphone'
  | 'camera'
  | 'unsupported'
  | 'config'
  | 'failed'
  /** The server refused the call: one of the two has blocked the other. */
  | 'blocked'
  /** There is nobody to call (the member no longer exists). */
  | 'unavailable';

export class CallProviderError extends Error {
  constructor(
    public readonly kind: CallErrorKind,
    message: string = kind,
  ) {
    super(message);
    this.name = 'CallProviderError';
  }
}

export interface JoinOptions {
  callId: string;
  /** The call's `providerSessionId`. */
  roomId: string;
  memberId: string;
  callType: CallType;
}

export interface CallSession {
  /** My camera and microphone (null for a provider without a preview). */
  localStream: MediaStream | null;
  /** Called with the other person's stream when it arrives, and null when it goes. */
  onRemoteStream(listener: (stream: MediaStream | null) => void): () => void;
  setMuted(muted: boolean): void;
  setCameraOn(on: boolean): void;
  /** More than one camera (a phone's front and rear, or two webcams): the call can switch between them. */
  canSwitchCamera: boolean;
  /** Changes to the next camera (front ↔ rear) without leaving the call; resolves to the stream now showing it. */
  switchCamera(): Promise<MediaStream | null>;
  leave(): Promise<void>;
}

export interface CallProvider {
  /**
   * Asks for the microphone (and camera for video) before anyone is rung, so
   * a blocked permission never leaves a call ringing. Throws `microphone`, `camera` or `unsupported`.
   */
  preflight(callType: CallType): Promise<void>;
  join(options: JoinOptions): Promise<CallSession>;
}

/** Same stream names as Android (`${roomId}-${memberId}`), so web and Android find each other. */
export function streamIdFor(roomId: string, memberId: string): string {
  return `${roomId}-${memberId}`;
}

/** This browser can capture a camera and microphone: it has getUserMedia and the page is served over HTTPS. */
export function canTakeCalls(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia) && globalThis.isSecureContext !== false;
}

/** Asks for the devices a call needs, then lets them go (the provider opens them again). */
export async function checkDevices(callType: CallType): Promise<void> {
  if (!canTakeCalls()) {
    console.error('[calls] this browser cannot capture media (no getUserMedia or not HTTPS)');
    throw new CallProviderError('unsupported');
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: callType === 'video',
    });
  } catch (error) {
    console.error('[calls] microphone/camera check failed', error);
    throw new CallProviderError(await blockedDevice(error, callType));
  }
  stream.getTracks().forEach((track) => track.stop());
}

const BLOCKED = new Set([
  'NotAllowedError',
  'SecurityError',
  'NotFoundError',
  'OverconstrainedError',
]);

/** Which device stopped the call: asking for the microphone alone tells microphone from camera. */
async function blockedDevice(error: unknown, callType: CallType): Promise<CallErrorKind> {
  const name = (error as { name?: string }).name ?? '';
  if (!BLOCKED.has(name)) return 'failed';
  if (callType !== 'video') return 'microphone';
  try {
    const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
    audioOnly.getTracks().forEach((track) => track.stop());
    return 'camera';
  } catch {
    return 'microphone';
  }
}

let provider: Promise<CallProvider> | null = null;

/** The configured provider; Zego's SDK is only downloaded when a call first needs it. */
export function getCallProvider(): Promise<CallProvider> {
  provider ??=
    getConfig().callProvider === 'fake'
      ? import('./fakeProvider').then((m) => m.fakeProvider)
      : import('./zegoProvider').then((m) => m.zegoProvider);
  return provider;
}
